import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { GroupService } from './group.service';
import { UserService } from './user.service';

/** `null` = Combined (show all). UUID string = that physician’s patients plus unassigned (`physicianId` empty); other doctors’ patients are hidden. */
export type CalendarPhysicianFilterSelection = string | null;

const STORAGE_PREFIX = 'calendarPhysicianFilter:';

@Injectable({
  providedIn: 'root',
})
export class CalendarPhysicianFilterService {
  private readonly selectedPhysicianIdSubject = new BehaviorSubject<CalendarPhysicianFilterSelection>(null);

  /** Emits `null` for Combined, or a user id when filtering by doctor. */
  readonly selectedPhysicianId$: Observable<CalendarPhysicianFilterSelection> =
    this.selectedPhysicianIdSubject.asObservable();

  constructor(
    private groupService: GroupService,
    private userService: UserService,
  ) {
    combineLatest([
      this.groupService.selectedGroup$,
      this.userService.userProfile$,
    ])
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            a[0]?.id === b[0]?.id &&
            a[0]?.calendarSplitByPhysician === b[0]?.calendarSplitByPhysician &&
            a[1]?.id === b[1]?.id &&
            a[1]?.userType === b[1]?.userType,
        ),
      )
      .subscribe(([group, user]) => {
        if (!group?.id || !group.calendarSplitByPhysician) {
          this.selectedPhysicianIdSubject.next(null);
          return;
        }
        const stored = this.readStored(group.id);
        if (stored !== undefined) {
          this.selectedPhysicianIdSubject.next(stored);
          return;
        }
        if (user?.userType === 'doctor' && user?.id) {
          this.selectedPhysicianIdSubject.next(String(user.id));
          this.persist(group.id, String(user.id));
        } else {
          this.selectedPhysicianIdSubject.next(null);
          this.persist(group.id, null);
        }
      });
  }

  getSelectedPhysicianId(): CalendarPhysicianFilterSelection {
    return this.selectedPhysicianIdSubject.getValue();
  }

  setSelectedPhysicianId(value: CalendarPhysicianFilterSelection, groupId?: string): void {
    this.selectedPhysicianIdSubject.next(value);
    const gid = groupId ?? this.groupService.group?.id;
    if (gid) {
      this.persist(gid, value);
    }
  }

  /** Combined = no filtering. With a doctor selected: keep unassigned + that doctor; drop bookings and events assigned to someone else. */
  applyAppointmentFilter<T extends { patientId?: string; physicianId?: string | null; Patient?: { id?: string } }>(
    appointments: T[] | null | undefined,
    splittingEnabled: boolean,
    selectedPhysicianId: CalendarPhysicianFilterSelection,
  ): T[] | null | undefined {
    if (!appointments?.length) {
      return appointments ?? null;
    }
    if (!splittingEnabled || selectedPhysicianId === null) {
      return appointments;
    }
    const selectedNorm = String(selectedPhysicianId).trim().toLowerCase();
    // Schedule events go through the same rule: one for another physician is hidden, one for the
    // whole practice (no physician) is kept.
    return appointments.filter((row) => {
      const r = row as T & { physician_id?: string | null };
      const raw = r.physicianId ?? r.physician_id;
      const pid =
        raw != null && String(raw).trim() !== ''
          ? String(raw).trim().toLowerCase()
          : null;
      if (pid == null || pid === '') {
        return true;
      }
      return pid === selectedNorm;
    });
  }

  filterWithGroup$<T extends { patientId?: string; physicianId?: string | null }>(
    source$: Observable<T[] | null>,
  ): Observable<T[] | null> {
    return combineLatest([source$, this.groupService.selectedGroup$, this.selectedPhysicianId$]).pipe(
      map(([rows, group, selected]) =>
        this.applyAppointmentFilter(rows, !!group?.calendarSplitByPhysician, selected) ?? null,
      ),
    );
  }

  private readStored(groupId: string): CalendarPhysicianFilterSelection | undefined {
    if (typeof localStorage === 'undefined') {
      return undefined;
    }
    const raw = localStorage.getItem(STORAGE_PREFIX + groupId);
    if (raw === null) {
      return undefined;
    }
    if (raw === '' || raw === 'combined') {
      return null;
    }
    return raw;
  }

  private persist(groupId: string, value: CalendarPhysicianFilterSelection): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    if (value === null) {
      localStorage.setItem(STORAGE_PREFIX + groupId, 'combined');
    } else {
      localStorage.setItem(STORAGE_PREFIX + groupId, value);
    }
  }
}
